import { Stack } from '@/components/layout';
import { drawerStateStore } from '@/components/ui/drawer/drawer-state-store';
import { STRINGS } from '@/constants/strings';
import { HomeView } from '@/features/home/components';
import { DashboardBackground } from '@/features/home/components/container';
import { useDashboard } from '@/features/home/hooks/use-dashboard';
import { useOverviewSessionUiStore } from '@/features/player-session/stores/overview-session-ui-store';
import { useAuthDrawerNavigation } from '@/hooks';
import { CommonActions, useFocusEffect } from '@react-navigation/native';
import { useCallback, useEffect, useEffectEvent } from 'react';
import { InteractionManager } from 'react-native';

export function DashboardScreen() {
  const { userFirstName, hasActiveSession, avatarSource, now, setNow } = useDashboard();
  const navigation = useAuthDrawerNavigation();
  const requestOverviewSessionRefresh = useOverviewSessionUiStore(s => s.requestRefresh);

  const prefetchScreens = useEffectEvent(() => {
    // Dashboard belongs to HomeStack; Player route lives in parent tab navigator.
    navigation.getParent()?.dispatch(
      CommonActions.preload('Player', {
        screen: 'PlayerList',
      }),
    );
  });

  useEffect(() => {
    // Defer preload until after the splash → dashboard transition + initial paint settles.
    // `preload` mounts the entire Player stack (Providers, list query, toolbar) which is heavy
    // enough to be visible as a 30–60ms frame if it lands in the same commit as Dashboard render.
    const interaction = InteractionManager.runAfterInteractions(() => {
      prefetchScreens();
    });
    return () => {
      interaction.cancel();
    };
  }, []);

  useFocusEffect(
    useCallback(() => {
      drawerStateStore.setDisplayState('visible');
      setNow(new Date());
      return () => {
        drawerStateStore.setDisplayState('hidden');
      };
    }, [setNow]),
  );

  const handleTrackProgress = () => {
    if (hasActiveSession) {
      requestOverviewSessionRefresh();
      navigation.navigate('HomeTabNavigator', {
        screen: 'Home',
        params: { screen: 'OverviewSession' },
      });
    } else {
      navigation.navigate('HomeTabNavigator', {
        screen: 'Home',
        params: { screen: 'SelectPlayer' },
      });
    }
  };

  return (
    <Stack flex={1}>
      <HomeView.Header avatarSource={avatarSource} roleName={STRINGS.home.header.coachLabel} />
      <DashboardBackground />
      <HomeView>
        <HomeView.WelcomeBanner userFirstName={userFirstName} now={now} />
        <HomeView.PlayerPathwayCard
          hasActiveSession={hasActiveSession}
          onTrackProgress={handleTrackProgress}
        />
      </HomeView>
    </Stack>
  );
}
